import React from 'react';
import {
  Card,
  CardContent,
  Typography,
  Chip,
  CardActionArea,
  Collapse,
  Box
} from '@mui/material';
import {
  NOTE_TYPE_COLOURS,
  NOTE_TYPE_LABEL_COLOURS
} from 'src/components/Constants';
import PushPinIcon from '@mui/icons-material/PushPin';

import StarIcon from '@mui/icons-material/Star';

import {
  PEDIGREE_RECORD_SURFACES,
  PREG_ACCENT,
  recordCardTint,
  recordCardTintBorder
} from './recordCardStyles';
import { fetusEventSubjectLabel } from '../../../pedigree-tool/pedigree/fetusEventSubject';
import useDateFormatter from 'src/hooks/useDateFormatter';
import {
  toLocalDateFromInstant,
  toLocalDateFromPickerOrImport
} from 'src/util/partialDate';

const TileCard = ({
  model,
  type,
  riskLabel,
  setIsEditing,
  setModalOpen,
  setNoteType,
  setCurrentModel,
  deepCopy,
  staffMemberOptions,
  showInFamily,
  highlightedPerson,
  setIsDisabled,
  onCustomClick,
  surfaces = PEDIGREE_RECORD_SURFACES,
  // The member this tile is listed under, used only to resolve a PRENATAL event's subject
  // (decision 21). Without it a fetal test in the mother's list reads as done to HER.
  carrier
}) => {
  const accent = NOTE_TYPE_COLOURS[model.noteType] || '#888';
  const labelColor = surfaces
    ? NOTE_TYPE_LABEL_COLOURS[model.noteType] || accent
    : accent;
  const [body] = React.useState({
    ...model,
    _id: '',
    createdAt: '',
    pinned: '',
    caution: '',
    noteType: ''
  });

  const [expanded, setExpanded] = React.useState(false);

  // The prenatal subject chip renders `dateOfRecord`, so it must honour the
  // deployment's date format like every other date surface (TGCLOUD-1707).
  const fmtDate = useDateFormatter();
  // Every record date on this tile is picked in a form (a local-anchored
  // instant) AND written by the spreadsheet importers (UTC midnight), so it is
  // read by toLocalDateFromPickerOrImport, as the record forms read it, then
  // shown in the configured format. `moment(value)` read each by its local
  // parts, so an imported date showed a day early at any negative offset.
  // `createdAt` is a server timestamp: a true instant.
  const recordDay = (value) => fmtDate(toLocalDateFromPickerOrImport(value));
  const createdDay = (value) => fmtDate(toLocalDateFromInstant(value));

  const handleExpandClick = () => {
    if (onCustomClick) {
      onCustomClick(model);
      return;
    }
    setIsEditing(true);
    setCurrentModel(model);
    setNoteType(type);
    setModalOpen(true);

    if (
      highlightedPerson?.familyMemberId !== model.familyMemberId &&
      !!setIsDisabled
    )
      setIsDisabled(true);
  };

  const isDate = (date) => {
    return !isNaN(Date.parse(date)) && Date.parse(date) > 0;
  };

  //Not showing the tilecard contents anymore, but the code is here just in case
  const renderField = (key) => {
    //let type = typeof body[key];
    //
    //if (isDate(body[key])) {
    //  if (key === 'appointmentTime')
    //    return <>{moment(body[key]).format('hh:mm')}</>;
    //  return <>{moment(body[key]).format(theme.dateFormat)}</>;
    //}
    //switch (type) {
    //  case 'object':
    //    return (
    //      <>
    //        {body[key] &&
    //          body[key].map((e, i) => (
    //            <Link
    //              key={i}
    //              href={fileLinks[e]}
    //              value={fileLinks[e]}
    //              onClick={(event) => {
    //                handleClick(event, fileLinks[e]);
    //              }}
    //            >
    //              <br />
    //              {e}
    //            </Link>
    //          ))}
    //      </>
    //    );
    //  case 'boolean':
    //    return <>{body[key] ? 'True' : 'False'}</>;
    //  case 'string':
    //    return (
    //      <>
    //        {body[key]}
    //        <Grid container>
    //          {key == 'trackingId' && (
    //            <Grid item xs={3} paddingTop="1.1rem">
    //              <QRCode value={body.trackingId} size={60} />
    //            </Grid>
    //          )}
    //          {key == 'trackingId' && (
    //            <Grid item xs={9} paddingTop="0.5rem">
    //              <Barcode
    //                value={body.trackingId}
    //                fontSize="0"
    //                height="60"
    //                width="2"
    //              />
    //            </Grid>
    //          )}
    //        </Grid>
    //      </>
    //    );
    //  default:
    //    return <>{body[key]}</>;
    //}
    return <>What</>;
  };

  // const SelectField = () => {
  //   switch (model.noteType) {
  //     case 'Referral':
  //       return model.referralDate;
  //     case 'Appointment':
  //       return model.appointmentDate;
  //     case 'Test':
  //       return model.dateRequested;
  //     case 'Sample':
  //       return model.dateTaken;
  //     case 'Address':
  //       return model.primaryAddress;
  //     case 'Clinical Note':
  //       return model.clinicalNoteDate;
  //     case 'Contact':
  //       return model.contactDate;
  //     case 'Consent':
  //       return model.consentDate || model.consentRequestDate;
  //     default:
  //       return model.createdAt;
  //   }
  // };

  // const getLabel = () => {
  //   const field = SelectField();
  //   if (type == 'Address') return field;
  //   return moment(field).format(theme.dateFormat);
  // };

  const getLabel = () => {
    switch (model.noteType) {
      case 'Clinical Note': {
        return (
          <>
            <div>Date: {recordDay(model.clinicalNoteDate)}</div>
            {model.clinician && (
              <div>
                Staff Member:
                {model.clinician}
              </div>
            )}
          </>
        );
      }

      case 'Address': {
        const primaryFlag = model.primaryAddress ? ' P' : '';
        const correspondenceFlag = model.correspondenceAddress ? ' C' : '';
        return (
          <>
            {model.addressLine1 && (
              <div>
                Address Line 1: {model.addressLine1}
                {primaryFlag} {correspondenceFlag}
              </div>
            )}
            <div>
              {(model.town || model.city) && (
                <span>Town/City: {model.town || model.city}</span>
              )}
              {model.postCode && (
                <span>
                  {model.town || model.city ? ', ' : ''}Postcode:{' '}
                  {model.postCode}
                </span>
              )}
              {model.country && (
                <span>
                  {model.town || model.city || model.postCode ? ', ' : ''}
                  Country: {model.country}
                </span>
              )}
            </div>
          </>
        );
      }

      case 'Appointment': {
        return (
          <>
            <div>
              {model.appointmentDate && (
                <span>Date: {recordDay(model.appointmentDate)}</span>
              )}
              {model.clinicName && (
                <span>
                  {model.appointmentDate ? ', ' : ''}Clinic Name:{' '}
                  {model.clinicName}
                </span>
              )}
            </div>
            <div>
              {model.staffMember && (
                <span>
                  Staff Member:{' '}
                  {
                    staffMemberOptions.find(
                      (staff) => staff.value === model.staffMember[0]
                    )?.label
                  }
                </span>
              )}
              {model.appointmentStatus && (
                <span>
                  {model.staffMember ? ', ' : ''}Appt Status:{' '}
                  {model.appointmentStatus}
                  {model.statusReason ? ` (${model.statusReason})` : ''}
                </span>
              )}
            </div>
          </>
        );
      }

      case 'Consent': {
        const dateValue = model.consentDate || model.consentRequestDate;
        return (
          <>
            <div>
              {dateValue && <span>Date: {recordDay(dateValue)}</span>}
              {model.response && (
                <span>
                  {dateValue ? '; ' : ''}Consent Response: {model.response}
                </span>
              )}
            </div>
            {model.consentType && <div>Consent Type: {model.consentType}</div>}
          </>
        );
      }
      case 'Tissue Pathology': {
        return (
          <>
            {model.tissueSite && <div>Tissue Site: {model.tissueSite}</div>}
            {model.morphology && <div>Morphology: {model.morphology}</div>}
            {model.pathReportDate && (
              <div>Report Date: {recordDay(model.pathReportDate)}</div>
            )}
          </>
        );
      }

      case 'Contact': {
        return (
          <>
            <div>
              {model.contactDate && (
                <span>Date: {recordDay(model.contactDate)}</span>
              )}
              {model.contactType && (
                <span>
                  {model.contactDate ? ', ' : ''}Contact Type:{' '}
                  {model.contactType}
                </span>
              )}
            </div>
            {model.staffMember && (
              <div>
                Staff Member:
                {
                  staffMemberOptions.find(
                    (staff) => staff.value === model.staffMember[0]
                  )?.label
                }
              </div>
            )}
          </>
        );
      }

      case 'Referral': {
        return (
          <>
            <div>
              {model.referralDate && (
                <span>Date: {recordDay(model.referralDate)}</span>
              )}
              {model.referralType && (
                <span>
                  {model.referralDate ? ', ' : ''}Referral Type:{' '}
                  {model.referralType}
                </span>
              )}
            </div>
            {model.referredBy && <div>Referral By: {model.referredBy}</div>}
          </>
        );
      }

      case 'Sample': {
        const statusText =
          !model.sampleStatus && !model.statusDate
            ? 'Sample Status is pending'
            : '';
        return (
          <>
            <div>
              {model.dateTaken && (
                <span>Date: {recordDay(model.dateTaken)}</span>
              )}
              {model.laboratory && (
                <span>
                  {model.dateTaken ? ', ' : ''}Laboratory: {model.laboratory}
                </span>
              )}
            </div>
            <div>
              {model.sampleStatus && (
                <span>Sample Status: {model.sampleStatus}</span>
              )}
              {model.statusDate && (
                <span>
                  {model.sampleStatus ? ', ' : ''}Status Date:{' '}
                  {recordDay(model.statusDate)}
                </span>
              )}
            </div>
            {statusText && (
              <div style={{ fontStyle: 'italic', fontSize: '0.8em' }}>
                {statusText}
              </div>
            )}
          </>
        );
      }

      case 'Treatment': {
        return (
          <>
            <div>
              {model.treatmentDate && (
                <span>Date: {recordDay(model.treatmentDate)}</span>
              )}
              {model.treatment && (
                <span>
                  {model.treatmentDate ? ', ' : ''}Treatment: {model.treatment}
                </span>
              )}
            </div>
            {model.treatmentEndDate && (
              <div>End Date: {recordDay(model.treatmentEndDate)}</div>
            )}
            {model.comment && <div>Comment: {model.comment}</div>}
          </>
        );
      }

      case 'Test': {
        const resultText =
          !model.testResult && !model.dateResultReceived
            ? 'Test Result is pending'
            : '';
        return (
          <>
            <div>
              {model.dateRequested && (
                <span>Date Requested: {recordDay(model.dateRequested)}</span>
              )}
              {(model.ageWhenTested || model.ageWhenTested === 0) && (
                <span>
                  {model.dateRequested ? ', ' : ''}
                  Age When Tested: {model.ageWhenTested}
                </span>
              )}
              {model.testName && (
                <span>
                  {model.dateRequested ? ', ' : ''}Test Name: {model.testName}
                </span>
              )}
            </div>
            <div>
              {model.testResult && <span>Test Result: {model.testResult}</span>}
              {model.dateResultReceived && (
                <span>
                  {model.testResult ? ', ' : ''}
                  Date Result Received: {recordDay(model.dateResultReceived)}
                </span>
              )}
            </div>
            {resultText && (
              <div style={{ fontStyle: 'italic', fontSize: '0.8em' }}>
                {resultText}
              </div>
            )}
          </>
        );
      }

      case 'Risk Score': {
        let summary = '';
        if (model.recordType === 'gail' && model.gailRiskScore != null) {
          summary = `Risk score: ${model.gailRiskScore}`;
        } else if (model.recordType === 'canrisk') {
          try {
            const pr = model.canriskResult?.pedigree_result?.[0];
            const tenYr =
              pr?.ten_yr_cancer_risk?.[0]?.['breast cancer risk']?.percent;
            const lifetime =
              pr?.lifetime_cancer_risk?.[0]?.['breast cancer risk']?.percent;
            if (tenYr != null && lifetime != null) {
              summary = `10yr: ${tenYr}% | Lifetime: ${lifetime}%`;
            }
          } catch (_) {
            /* ignore */
          }
          if (!summary) {
            summary = model.canriskResult
              ? 'Scores calculated'
              : 'Pedigree downloaded';
          }
        } else if (model.recordType === 'prs') {
          const pr = model.prsResult;
          const label = pr?.trait || pr?.pgsId;
          const parts = [
            pr?.zscore != null ? `z=${pr.zscore}` : null,
            pr?.percentile != null ? `${pr.percentile}th percentile` : null
          ].filter(Boolean);
          summary = label ? [label, ...parts].join(' — ') : 'PRS record saved';
        } else if (model.recordType === 'questionnaire') {
          summary = 'Submitted from questionnaire';
        }
        return (
          <>
            <div>{summary}</div>
            {model.createdAt && <div>{createdDay(model.createdAt)}</div>}
          </>
        );
      }

      case 'Process': {
        return (
          <>
            <div>
              {model.processType && <span>Type: {model.processType}</span>}
              {model.processStatus && (
                <span>
                  {model.processType ? ', ' : ''}Status: {model.processStatus}
                </span>
              )}
            </div>
            {(model.processStartDate || model.endDate) && (
              <div>
                {model.processStartDate && (
                  <span>Start: {recordDay(model.processStartDate)}</span>
                )}
                {model.endDate && (
                  <span>
                    {model.processStartDate ? ', ' : ''}End:{' '}
                    {recordDay(model.endDate)}
                  </span>
                )}
              </div>
            )}
            {model.serviceProvider && (
              <div>Service Provider: {model.serviceProvider}</div>
            )}
          </>
        );
      }

      default: {
        return model.createdAt ? createdDay(model.createdAt) : '';
      }
    }
  };

  const personSummary = () => {
    const person = (deepCopy || []).find(
      (data) =>
        data.familyMemberId === model.familyMemberId ||
        data.proBandId === model.familyMemberId
    );

    if (!person) return '';

    const { firstName, lastName, upn } = person;
    const fullName = [firstName, lastName].filter(Boolean).join(' ');

    return `${fullName}${fullName ? ', ' : ''}UPN ${
      person.proBandId ? '1' : upn
    }`;
  };

  return (
    <Card
      sx={{
        border: model.caution
          ? '1px solid red'
          : `1px solid ${surfaces ? recordCardTintBorder(accent) : '#e0e0e0'}`,
        borderLeft: `3px solid ${NOTE_TYPE_COLOURS[model.noteType] || '#ccc'}`,
        backgroundColor: surfaces ? recordCardTint(accent) : '#fff',
        boxShadow: 'none',
        borderRadius: '6px',
        ...(surfaces && {
          transition: 'transform .15s ease, box-shadow .15s ease',
          '&:hover': {
            transform: 'translateY(-1px)',
            boxShadow: '0 4px 12px rgba(31, 42, 51, .13)'
          }
        })
      }}
    >
      <CardActionArea onClick={handleExpandClick}>
        <CardContent sx={{ py: 1, px: 1.5, '&:last-child': { pb: 1 } }}>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between'
            }}
          >
            <Typography
              sx={{
                fontSize: '10px',
                fontWeight: 700,
                letterSpacing: '0.06em',
                textTransform: 'uppercase',
                color: labelColor,
                lineHeight: 1
              }}
            >
              {type}
              {model.caution && <StarIcon fontSize="sm" sx={{ ml: 0.5 }} />}
              {model.pinned && <PushPinIcon fontSize="sm" sx={{ ml: 0.5 }} />}
            </Typography>
            {/* Same label, same helper as the Browse-all card — so the two surfaces cannot disagree
                about whose event this is. */}
            {fetusEventSubjectLabel(model, carrier, fmtDate) && (
              <Chip
                label={fetusEventSubjectLabel(model, carrier, fmtDate)}
                size="small"
                sx={{
                  height: 18,
                  ml: 1,
                  fontSize: '10px',
                  fontWeight: 600,
                  color: PREG_ACCENT,
                  backgroundColor: recordCardTint(PREG_ACCENT),
                  border: `1px solid ${recordCardTintBorder(PREG_ACCENT)}`
                }}
              />
            )}
            {riskLabel && (
              <Chip
                label={riskLabel}
                size="small"
                sx={{
                  height: 18,
                  fontSize: '10px',
                  fontWeight: 700,
                  ml: 1
                }}
              />
            )}
          </Box>
          <Typography variant="body2" component="div" sx={{ mt: 0.5 }}>
            {getLabel()}
          </Typography>
          {showInFamily && (
            <Typography
              variant="body2"
              component="div"
              sx={{
                fontStyle:
                  highlightedPerson?.familyMemberId === model?.familyMemberId
                    ? 'italic'
                    : 'normal',

                color: 'text.secondary',
                mt: 0.25
              }}
            >
              {highlightedPerson?.familyMemberId === model?.familyMemberId
                ? 'Current person'
                : personSummary()}
            </Typography>
          )}

          <Collapse in={expanded}>
            {Object.keys(body).map((key) =>
              body[key] != '' && body[key] != null ? (
                <Typography
                  key={key}
                  variant="body1"
                  sx={{ borderBottom: '1px solid #ccc', py: 1 }}
                >
                  <span style={{ fontWeight: 'bold' }}>{key} </span>
                  {renderField(key)}
                </Typography>
              ) : null
            )}
          </Collapse>
        </CardContent>
      </CardActionArea>
    </Card>
  );
};

export default TileCard;
